import { catchError, filter, fromEvent, map, merge, of, switchMap, tap, throttleTime } from 'rxjs';
import { cloneDeep } from 'lodash-es';
import { dispatch, RootState } from '../store';
import { EMPTY_ACTION } from './emptyAction';
import { Epic, ofType } from 'redux-observable';
import { getSpecificationFilters } from '../../apis/categories';
import { setPageLoader } from '../../constants/loader';
import { SpecificationFilter } from '../../types/filters';
import {
  clearSpecFilters,
  initSpecFiltersState,
  loadingSpecFiltersError,
  loadingSpecFilters,
  loadingSpecFiltersSuccess,
  setSpecFilters,
  updateSpecFilter,
} from '../slice/specFiltersSlice';

const PAGE_LOADER_KEY = 'loadSpecFiltersEpic';
const FILTERS_SEARCH_PARAM = 'filters';
const SEARCH_PARAM_KEY_VALUE_SEPARATOR = '::';
const SEARCH_PARAM_ITEM_SEPARATOR = '||';
const SEARCH_PARAM_PAIR_SEPARATOR = '&&';

export const loadSpecFiltersEpic: Epic = (action$, state$) =>
  action$.pipe(
    ofType(
      initSpecFiltersState.type,
      setSpecFilters.type,
      updateSpecFilter.type,
      clearSpecFilters.type,
    ),
    tap(() => {
      dispatch(loadingSpecFilters());
      setPageLoader(PAGE_LOADER_KEY, true);
    }),
    throttleTime(250, undefined, { leading: false, trailing: true }),
    switchMap(async () => {
      try {
        const {
          categoryId,
          filters: filters,
          isInitialised,
        } = (state$.value as RootState).specFilters;

        if (!categoryId) {
          return EMPTY_ACTION('Error at loadFiltersEpic: categoryId not found');
        }

        const searchParams = new URLSearchParams(window.location.search);
        const filterParam = searchParams.get(FILTERS_SEARCH_PARAM);

        const addFilterStatusFromSearchParams = (data?: SpecificationFilter[] | null) => {
          if (!data) {
            return;
          }
          const specFilters = cloneDeep(data);
          if (filterParam) {
            const decodedFilters = decodeURIComponent(filterParam);
            const selectedFilterEntries = decodedFilters.split(SEARCH_PARAM_PAIR_SEPARATOR);
            const selectedFilters: { [key in string]: string[] } = {};
            selectedFilterEntries.forEach((entry) => {
              const [key, values] = entry.split(SEARCH_PARAM_KEY_VALUE_SEPARATOR);
              if (key && values) {
                selectedFilters[key] = values.split(SEARCH_PARAM_ITEM_SEPARATOR);
              }
            });
            specFilters.forEach((filter) => {
              filter.options.forEach((option) => {
                option.isSelected = (selectedFilters[filter.spec_id] ?? []).includes(option.label)
                  ? true
                  : false;
              });
            });
          }
          return specFilters;
        };

        // If filters are not initialised and url has filter selections
        // get the initial filters list and pass it as parameter
        const filtersPayload =
          !isInitialised && filterParam
            ? addFilterStatusFromSearchParams((await getSpecificationFilters(categoryId)).data)
            : filters;

        const response = await getSpecificationFilters(categoryId, filtersPayload);

        return loadingSpecFiltersSuccess({
          data: addFilterStatusFromSearchParams(response.data) as SpecificationFilter[],
          total_count: response.total_count,
        });
      } catch (error) {
        return loadingSpecFiltersError();
      } finally {
        setPageLoader(PAGE_LOADER_KEY, false);
      }
    }),
    catchError(() => {
      setPageLoader(PAGE_LOADER_KEY, false);
      return of(EMPTY_ACTION('Error at loadFiltersEpic'));
    }),
  );

export const updateSpecFilterSearchParamsEpic: Epic = (action$, state$) =>
  action$.pipe(
    ofType(setSpecFilters.type, updateSpecFilter.type, clearSpecFilters.type),
    filter(() => {
      if (!(state$.value as RootState).specFilters.isInitialised) {
        return false;
      }
      return true;
    }),
    throttleTime(100, undefined, { leading: false, trailing: true }),
    map(() => {
      const { filters: filters } = (state$.value as RootState).specFilters;
      const searchParams = new URLSearchParams();

      const appliedFilters: string[] = [];
      filters?.forEach((filter) => {
        const selectedOptions = filter.options
          .filter((option) => option.isSelected)
          .map((option) => option.label);

        if (selectedOptions.length) {
          appliedFilters.push(
            `${filter.spec_id}${SEARCH_PARAM_KEY_VALUE_SEPARATOR}${selectedOptions.join(
              SEARCH_PARAM_ITEM_SEPARATOR,
            )}`,
          );
        }
      });

      if (appliedFilters.length) {
        searchParams.set(
          FILTERS_SEARCH_PARAM,
          encodeURIComponent(appliedFilters.join(SEARCH_PARAM_PAIR_SEPARATOR)),
        );
      }

      // Update URL without reloading
      const queryString = searchParams.toString();
      const nextUrl = `${window.location.pathname}${queryString ? `?${queryString}` : ''}`;
      window.history.pushState({}, '', nextUrl);

      return EMPTY_ACTION('Filters search params updated');
    }),
    catchError(() => {
      return of(EMPTY_ACTION('Error at updateFilterSearchParamsEpic'));
    }),
  );

export const reloadSpecFiltersOnPageNavigationEpic = () =>
  merge(fromEvent(window, 'popstate'), fromEvent(window, 'pushstate')).pipe(
    tap(() => {
      const newUrl = window.location.pathname;
      const categoryPagePattern = /^\/rent\/categories\/[^/]+$/;

      if (categoryPagePattern.test(newUrl)) {
        window.location.reload();
      }
    }),
    map(() => EMPTY_ACTION('Page reloaded on back/forward button click for categories page')),
    catchError(() => {
      return of(EMPTY_ACTION('Error at reloadSpecFiltersOnPageNavigationEpic'));
    }),
  );
